// registry.js — the single merged source of truth for Atlas v3.
//
// The academic base graph (graph-data.js) is ALWAYS present. The frontier layer
// (frontier-data.js) is an additive OVERLAY: its nodes/edges are merged in here
// and tagged layer:'frontier', but they stay hidden until the user turns on
// "Show the frontier". Core nodes/edges are tagged layer:'core'.
//
// Every consumer (pathfinder, graph, learn, app) imports from THIS module so the
// two layers stay perfectly consistent.

import { DOMAINS, NODES, PREREQS, CROSS, GOALS } from './graph-data.js';
import {
  TIERS, FLAG_LABELS, FRONTIER_DOMAIN,
  FRONTIER_NODES, FRONTIER_PREREQS, FRONTIER_CROSS, FRONTIER_GOALS,
} from './frontier-data.js';

// Re-export the tier/flag metadata so other modules don't double-import.
export { TIERS, FLAG_LABELS };

// ---- Merged domains (core + the single 'frontier' bucket) ----
export const ALL_DOMAINS = { ...DOMAINS, ...FRONTIER_DOMAIN };

// ---- Merged nodes, each tagged with its layer ----
export const ALL_NODES = [
  ...NODES.map(n => ({ ...n, layer: 'core' })),
  ...FRONTIER_NODES.map(n => ({ ...n, layer: 'frontier' })),
];

// ---- Merged prereq edges: [from, to, strength, layer] ----
export const ALL_PREREQS = [
  ...PREREQS.map(([f, t, s]) => [f, t, s, 'core']),
  ...FRONTIER_PREREQS.map(([f, t, s]) => [f, t, s, 'frontier']),
];

// ---- Merged cross edges ----
// Core cross edges are simple [a, b]. Frontier cross edges are rich objects
// carrying tier/evidence/grounding. We normalize both into one shape:
//   { a, b, layer, tier?, grounding?, evidence? }
export const ALL_CROSS = [
  ...CROSS.map(([a, b]) => ({ a, b, layer: 'core' })),
  ...FRONTIER_CROSS.map(c => ({
    a: c.a, b: c.b, layer: 'frontier',
    tier: c.tier, grounding: !!c.grounding, evidence: c.evidence,
  })),
];

// ---- Merged goals (frontier goals carry frontier:true) ----
export const ALL_GOALS = [
  ...GOALS.map(g => ({ ...g, frontier: false })),
  ...FRONTIER_GOALS,
];

// ---- Lookups ----
export const nodeById = Object.fromEntries(ALL_NODES.map(n => [n.id, n]));
export function isFrontier(id) { return nodeById[id] && nodeById[id].layer === 'frontier'; }

// Node-level evidence (only frontier nodes have it).
export function evidenceForNode(id) {
  const n = nodeById[id];
  return n && n.evidence ? n.evidence : null;
}

// Edge-level evidence: find a frontier cross edge between a and b (order-agnostic).
export function evidenceForEdge(a, b) {
  const e = ALL_CROSS.find(c => c.layer === 'frontier' &&
    ((c.a === a && c.b === b) || (c.a === b && c.b === a)));
  return e || null;
}

// All frontier bridges touching a node, with their evidence (for the inspector).
export function bridgesForNode(id) {
  return ALL_CROSS
    .filter(c => c.layer === 'frontier' && (c.a === id || c.b === id))
    .map(c => ({
      otherId: c.a === id ? c.b : c.a,
      tier: c.tier,
      grounding: c.grounding,
      evidence: c.evidence,
    }));
}

// Tier color helper.
export function tierColor(t) { return (TIERS[t] || {}).c || '#888'; }
export function tierName(t) { return (TIERS[t] || {}).name || ''; }
export function tierShort(t) { return (TIERS[t] || {}).short || ('T' + t); }
